import React, { useState, useRef } from 'react';
import { cacheTransactions } from './txCache';
import { cacheUser, readCachedUser } from './userCache';
import { CARD_DETAIL_IMAGES } from './cardDetails';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import KeyScreen from './KeyScreen';
import PasswordScreen from './PasswordScreen';
import MainPage from './MainPage';
import CreditPage from './CreditPage';
import SavingsPage from './SavingsPage';
import MorePage from './MorePage';
import MarketPage from './MarketPage';
import MarketTabScreen from './MarketTabScreen';
import TransactionsHistory from './TransactionsHistory';
import TransactionsDetails from "./TransactionsDetails";
import StatisticsPage from "./StatisticsPage";
import DesktopBlockedScreen from './DesktopBlockedScreen';
import AddToHomeScreenHint from './AddToHomeScreenHint';
import { preloadImage } from './ImageWithCache';
import Watermark from './Watermark';
import { LogoutContext } from './logout';
import Navbar from './Navbar';
import './fonts.css';
import { ref, get, set } from 'firebase/database';
import { database } from './firebaseConfig';
import { getStoredChatId, clearStoredChatId } from './chatId';
import { BLOCK_DESKTOP, isMobileDevice } from './deviceGate';
import { REQUIRE_STANDALONE, isStandaloneDisplay } from './pwaGate';


// Navbar лишається змонтованим (не пере-створюється при кожній зміні
// роуту), поки користувач лишається серед екранів із нижнім таб-баром —
// так перемикання на /market і назад робиться crossfade'ом, а не
// миттєвою підміною. На /history та /transaction/:id таб-бару не було
// і раніше, тому там Navbar просто не рендериться.
// /statistics — навбар лишається під сторінкою статистики (вона виїжджає поверх)
const NAVBAR_ROUTES = ['/', '/credit', '/savings', '/more', '/market', '/history', '/statistics'];

function AppRoutes() {
  const location = useLocation();
  // Деталі транзакції — слайд-оверлей поверх головного екрана, тож навбар
  // теж лишаємо (видно на фоні під час анімації, як і в історії).
  const showNavbar = NAVBAR_ROUTES.includes(location.pathname)
    || location.pathname.startsWith('/transaction/')
    || location.pathname.startsWith('/market/');

  // MainPage — фон для деталей транзакції та історії. Рендеримо її поза <Routes>,
  // щоб при переході / → /transaction/:id → / вона не перемонтовувалась:
  // інакше список операцій і картинки перебудовуються з нуля й «блимають».
  const isTxRoute = location.pathname.startsWith('/transaction/');
  const showMainPage = location.pathname === '/'
    || location.pathname === '/history'
    || location.pathname === '/statistics'
    || isTxRoute;
  // Так само історія: деталі, відкриті з неї, рендеряться поверх того ж
  // екземпляра (як статичний фон), а не поверх нового.
  const showHistory = location.pathname === '/history'
    || (isTxRoute && location.state?.from === 'history');
  // І статистика: деталі операції з категорії відкриваються поверх неї, а
  // сама вона не перемонтовується — лишаються обрані категорія, період, картки
  const showStatistics = location.pathname === '/statistics'
    || (isTxRoute && location.state?.from === 'statistics');

  return (
    <>
      {showNavbar && <Navbar />}
      {showMainPage && <MainPage />}
      {showHistory && <TransactionsHistory asBackdrop={isTxRoute} />}
      {showStatistics && <StatisticsPage />}
      <Routes>
        <Route path="/" element={null} />
        <Route path="/credit" element={<CreditPage />} />
        <Route path="/savings" element={<SavingsPage />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/market" element={<MarketPage />} />
        <Route
          path="/market/cabinet"
          element={
            <MarketTabScreen
              pageTitle="Кабінет"
              tabs={[
                { label: 'Мої замовлення', title: 'Замовлення відсутні' },
                { label: 'Обрані товари', title: 'Обраних товарів немає' },
              ]}
            />
          }
        />
        <Route
          path="/market/purchases"
          element={
            <MarketTabScreen
              tabs={[
                {
                  label: 'Покупки',
                  title: 'Ви ще нічого не придбали',
                  subtitle: 'Тут буде історія ваших покупок, але спершу вам треба щось купити 😄',
                },
                { label: 'Збережені', title: 'Збережених оголошень немає' },
              ]}
            />
          }
        />
        <Route
          path="/market/cart"
          element={
            <MarketTabScreen
              heading="Ваш кошик"
              empty={{ title: 'Кошик порожній', subtitle: 'Додайте товари, щоб оформити замовлення' }}
            />
          }
        />
        {/* Деталі транзакції — слайд справа поверх екрана-джерела як фону */}
        <Route path="/transaction/:id" element={<TransactionsDetails />} />
        {/* Статистика рендериться вище, поза <Routes> (фон для деталей операції) */}
        <Route path="/statistics" element={null} />
        {/* Історія (слайд-ап оверлей) і MainPage під нею рендеряться вище, поза <Routes> */}
        <Route path="/history" element={null} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// Проверки идут до монтирования AppInner, поэтому пока они не пройдены
// не запускается ни один хук/эффект настоящего приложения (Firebase и т.д.)
function App() {
  if (BLOCK_DESKTOP && !isMobileDevice()) {
    return <DesktopBlockedScreen />;
  }
  if (REQUIRE_STANDALONE && isMobileDevice() && !isStandaloneDisplay()) {
    return <AddToHomeScreenHint />;
  }
  return <AppInner />;
}

function AppInner() {
  const [chatId, setChatId] = useState(() => getStoredChatId());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const preloadRef = useRef(null); // will hold preload Promise

  const balanceKey = `balance_raw_${chatId}`;
  const ratesKey = 'currency_rates_cache';
  const ratesTtl = 60 * 60 * 1000; // час — как у бота, публичное API monobank даёт максимум 1 запрос/мин на IP
  const ratesFirebasePath = 'meta/currencyRates';
  const images = [
    // MainPage
    "/mono-avatar.webp","/noti.png","/header-icons/gift.png","/header-icons/cat.png","/header-icons/stats.png","/plus.png",
    "/cards.png","/card-btn.png","/iban-btn.png",
    "/another-btn.png","/icons/question.png",
    "/icons/printer.png","/icons/document.png",
    "/market-icons/phone.webp","/market-icons/ipad.webp","/market-icons/macbook.webp",
    "/market-icons/pods.webp","/market-icons/futo.webp","/market-icons/ring.webp",
    "/market-icons/tv.webp",
    // Усі картки / Відкрити картку
    "/card-icons/black.png","/card-icons/white.png","/card-icons/usd.png",
    "/card-icons/eur.png","/card-icons/cashback.png","/open-card-cat.png",
    ...CARD_DETAIL_IMAGES,
    // PasswordScreen
    "/imgs/eraser.png",
    // Navbar
    "/nav-cards.png","/credit.png","/saving.png","/market.png","/more.png",
    "/credit-icons/credit-nav-icon.webp","/card-unactive.webp","/saving-active.webp",
    "/other-icons/other-icon.webp",
    // CreditPage
    "/credit-icons/credit-setings.webp","/credit-icons/credit-info.webp",
    "/credit-icons/credit-paw.webp","/credit-icons/credit-cat.webp",
    "/credit-icons/credit-credit.webp","/credit-icons/credit-calendar.webp",
    // SavingsPage
    "/safe-icons/safe-icon.webp","/safe-icons/safe-jar.webp","/safe-icons/safe-gerb.webp",
    "/safe-icons/safe-archive.webp","/safe-icons/safe-info.webp",
    // MorePage
    "/other-icons/other-invite.webp","/other-icons/other-support.webp","/other-icons/other-scan.webp",
    "/other-icons/other-car.webp","/other-icons/other-phone.webp","/other-icons/other-fuel.webp",
    "/other-icons/other-coupon.webp","/other-icons/other-security.webp","/other-icons/other-sign.webp",
    // MarketPage
    "/market-icons/mono-market.webp","/market-icons/market-bag.webp","/market-icons/market-bag-inactive.webp",
    "/market-icons/market-love.webp","/market-icons/market-love-active.webp",
    "/market-icons/market-cart.webp","/market-icons/market-cart-active.webp","/market-icons/market-bazar.webp",
    // Transactions / TransactionsHistory
    "/arrow.png","/cross.png","/search.png",
    // TransactionsDetails
    "/arrow-back.png","/pencil.png","/pdf.png",
    "/food-header.png","/transfer-header.png","/transaction-header.png",
    "/clothes-header.png","/other-header.png"
  ];

  // курсы валют — публичное монобанковское API жёстко лимитирует частоту
  // (1 запрос/мин НА IP, а не на клиента), поэтому если каждый браузер бьёт
  // по нему независимо, лимит выбивается почти сразу при нескольких
  // одновременных заходах. Поэтому сначала читаем общий кеш в Firebase
  // (его мог обновить любой другой клиент или бот — это бесплатно и не
  // подпадает под лимит Monobank), и только если он тоже устарел — идём
  // напрямую в Monobank и делимся результатом через тот же узел.
  async function loadRates() {
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(ratesKey)); } catch (e) { /* ignore */ }
    if (cached && Date.now() - cached.fetchedAt < ratesTtl) return;

    try {
      const snap = await get(ref(database, ratesFirebasePath));
      const shared = snap.val();
      if (shared && Date.now() - shared.fetchedAt < ratesTtl) {
        try { localStorage.setItem(ratesKey, JSON.stringify(shared)); } catch (e) { /* ignore */ }
        return;
      }
    } catch (e) { /* Firebase недоступен — пробуем сходить в Monobank напрямую */ }

    try {
      const res = await fetch('https://api.monobank.ua/bank/currency');
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const usd = data.find((r) => r.currencyCodeA === 840 && r.currencyCodeB === 980);
      const eur = data.find((r) => r.currencyCodeA === 978 && r.currencyCodeB === 980);
      if (!usd && !eur) return;

      const next = {
        usdBuy: usd?.rateBuy ?? null,
        usdSell: usd?.rateSell ?? null,
        eurBuy: eur?.rateBuy ?? null,
        eurSell: eur?.rateSell ?? null,
        fetchedAt: Date.now(),
      };
      try { localStorage.setItem(ratesKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
      try { await set(ref(database, ratesFirebasePath), next); } catch (e) { /* ignore */ }
    } catch (e) {
      // оффлайн или лимит запросов (TMR) — вместо «—» берём хоть какой-то
      // общий кеш из Firebase, даже устаревший
      try {
        const snap = await get(ref(database, ratesFirebasePath));
        const shared = snap.val();
        if (shared) {
          try { localStorage.setItem(ratesKey, JSON.stringify(shared)); } catch (e2) { /* ignore */ }
        }
      } catch (e2) { /* ignore */ }
    }
  }

  async function preload() {
    let userPhoto = readCachedUser(chatId).photoUrl;
    try {
      const uSnap = await get(ref(database, `users/${chatId}`));
      userPhoto = cacheUser(chatId, uSnap.val() || {}).photoUrl;
    } catch (e) { /* ignore */ }

    try {
      const bSnap = await get(ref(database, `balance/${chatId}/value`));
      const bVal = bSnap.val();
      if (bVal !== undefined && bVal !== null) {
        try { localStorage.setItem(balanceKey, String(bVal)); } catch (e) { /* ignore */ }
      }
    } catch (e) { /* ignore */ }

    await loadRates();

    let txImages = [];
    try {
      const tSnap = await get(ref(database, `transactions/${chatId}`));
      const tVal = tSnap.val();
      let arr = [];
      if (tVal !== null && tVal !== undefined) {
        if (Array.isArray(tVal)) arr = tVal;
        else if (typeof tVal === 'object') arr = Object.keys(tVal).map(k => ({ id: k, ...tVal[k] }));
      }
      cacheTransactions(chatId, arr);
      txImages = [...new Set(arr.map(tx => tx && tx.img).filter(Boolean))];
    } catch (e) { /* ignore */ }

    await Promise.all([...images, ...txImages, userPhoto].map(s => preloadImage(s)));
  }

  // start preload immediately once chatId is known (guarded)
  if (chatId && !preloadRef.current) {
    // start async preload and store promise so we can await it later
    preloadRef.current = preload();
  }

  // when password screen signals success, wait for preload to finish first
  async function handleAuthOnSuccess() {
    try {
      if (!preloadRef.current) {
        preloadRef.current = preload();
      }
      await preloadRef.current;
    } catch (e) {
      // ignore preload errors, still allow login
    }
    setIsAuthenticated(true);
  }

  if (!chatId) {
    return <KeyScreen onPaired={setChatId} />;
  }

  if (!isAuthenticated) {
    return <PasswordScreen chatId={chatId} onSuccess={() => handleAuthOnSuccess()} />;
  }

  // «Вийти» в налаштуваннях — забуваємо прив'язку й повертаємось на екран
  // коду входу; preload скидаємо, щоб після нового входу він пішов заново
  // для нового chatId
  function logout() {
    clearStoredChatId();
    preloadRef.current = null;
    setIsAuthenticated(false);
    setChatId(null);
  }

  return (
    <LogoutContext.Provider value={logout}>
      <Watermark chatId={chatId} />
      <Router>
        <AppRoutes />
      </Router>
    </LogoutContext.Provider>
  );
}

export default App;
